import { CommunitySidebarSkeleton } from "@circle-react-uikit/SkeletonLoader/skeletons/CommunitySidebarV2";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useSpaceGroupsContext } from "@circle-react/contexts/spaceGroupsContext";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { fetchRequest } from "@circle-react/helpers/backendRequestHelpers";
import {
  canCreateSpaces,
  isCollapsed,
} from "@circle-react/helpers/spaceGroupHelpers";
import { isSpaceVisibleOnSidebar } from "@circle-react/helpers/spaceHelpers";
import { useTailwindLgScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useSpaceSidebarNotifications } from "@circle-react/hooks/useSpaceSidebarNotifications";
import type { Space, SpaceGroup } from "@circle-react/types";
import { arrayMoveImmutable as arrayMove } from "array-move";
import { useCallback, useEffect, useState } from "react";

import { useCircleV4 } from "@/react/hooks/useCircleV4";
import { useIsLoadingAppData } from "@/react/hooks/useIsLoadingAppData";

import { SidebarV2 } from "../SidebarV2";
import { SidebarV3 } from "../SidebarV3";

import "./styles.scss";
import { DndContext } from "./DndContext";

export interface SpaceGroupWithSpaces extends SpaceGroup {
  collapsed: boolean;
  spaces: Space[];
}

export type SidebarSpaceGroups = SpaceGroupWithSpaces[];

export interface SpaceGroupDragProperties {
  index: number | null;
  isDragging: boolean;
}

export const SideBar = () => {
  const {
    helpers: { isDataLoading: isSpaceGroupsDataLoading },
    records: spaceGroups,
    actions: { refresh: refreshSpaceGroups },
  } = useSpaceGroupsContext();

  const {
    helpers: { isDataLoading: isSpacesDataLoading, spaceVisibleOnSidebar },
    actions: { refresh: refreshSpaces },
  } = useSpacesContext();

  const { currentCommunityMember, isLoading: isCommunityLoading } =
    usePunditUserContext();

  const spaces = spaceVisibleOnSidebar();
  const isLoading =
    isSpacesDataLoading() || isSpaceGroupsDataLoading() || isCommunityLoading;

  const computeSpaceGroupWithSpaces = useCallback((): SidebarSpaceGroups => {
    if (isLoading) {
      return [];
    }

    const allSpaceGroupsWithSpaces = spaceGroups.map(
      (spaceGroup: SpaceGroup): SpaceGroupWithSpaces => {
        const filteredSpaces = spaces.filter(
          (space: Space) =>
            space.space_group_id === spaceGroup.id &&
            isSpaceVisibleOnSidebar({ space, spaceGroup }),
        );

        return {
          ...spaceGroup,
          collapsed: isCollapsed({
            currentCommunityMember,
            spaceGroupId: spaceGroup.id,
          }),
          spaces: filteredSpaces,
        };
      },
    );

    // We're only showing space groups that have spaces or where the member can
    // interact with the space group (create spaces or join spaces).
    return allSpaceGroupsWithSpaces.filter(
      (spaceGroup: SpaceGroupWithSpaces) =>
        spaceGroup.spaces.length > 0 ||
        spaceGroup.hide_non_member_spaces_from_sidebar ||
        canCreateSpaces({ spaceGroup }),
    );
  }, [isLoading, spaces, spaceGroups, currentCommunityMember]);

  // Initialize synchronously so a remount with cached data doesn't paint an
  // empty sidebar before the syncing effect below runs.
  const [spaceGroupWithSpaces, setSpaceGroupWithSpaces] =
    useState<SidebarSpaceGroups>(computeSpaceGroupWithSpaces);

  const { refetchSpaceNotifications } = useSpaceSidebarNotifications();

  useEffect(() => {
    currentCommunityMember && void refetchSpaceNotifications();
  }, [currentCommunityMember, refetchSpaceNotifications]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setSpaceGroupWithSpaces(computeSpaceGroupWithSpaces());
  }, [isLoading, computeSpaceGroupWithSpaces]);

  const applySpaceReorder = async ({ spaceGroupId, orderedSpaceIds }: any) => {
    try {
      await fetchRequest(`/space_groups/${spaceGroupId}/reorder_spaces`, {
        method: "POST",
        body: JSON.stringify({
          order_array: orderedSpaceIds,
        }),
      });
      await refreshSpaces();
    } catch (err) {
      console.error("Error while reordering space: ", err);
    }
  };

  const onSpaceSortEnd = ({
    activeId,
    overId,
    spaceGroupId,
  }: {
    activeId: number;
    overId: number;
    spaceGroupId: number;
  }) => {
    const spaceGroup = spaceGroupWithSpaces.find(
      group => group.id === spaceGroupId,
    );
    if (!spaceGroup) return;

    const oldIndex = spaceGroup.spaces.findIndex(
      space => space.id === activeId,
    );
    const newIndex = spaceGroup.spaces.findIndex(space => space.id === overId);

    const reorderedData = spaceGroupWithSpaces.map(currentGroup => {
      if (currentGroup.id == spaceGroup.id) {
        const spaces: Space[] = arrayMove(
          spaceGroup.spaces,
          oldIndex,
          newIndex,
        );

        return { ...currentGroup, spaces };
      }

      return currentGroup;
    });

    setSpaceGroupWithSpaces(reorderedData);

    const orderedSpaceIds = reorderedData
      .find(currentGroup => currentGroup.id === spaceGroup.id)
      ?.spaces.map(space => space.id);

    void applySpaceReorder({ spaceGroupId: spaceGroup.id, orderedSpaceIds });
  };

  const applySpaceGroupReorder = async (orderedSpaceGroupIds: any) => {
    try {
      await fetchRequest("/space_groups/reorder", {
        method: "POST",
        body: JSON.stringify({
          order_array: orderedSpaceGroupIds,
        }),
      });
      refreshSpaceGroups();
    } catch (err) {
      console.error("Error while reordering space groups: ", err);
    }
  };

  const onSpaceGroupSortEnd = ({
    activeId,
    overId,
  }: {
    activeId: number;
    overId: number;
  }) => {
    const oldIndex = spaceGroupWithSpaces.findIndex(
      group => group.id === activeId,
    );
    const newIndex = spaceGroupWithSpaces.findIndex(
      group => group.id === overId,
    );
    const reorderedData = arrayMove(spaceGroupWithSpaces, oldIndex, newIndex);
    setSpaceGroupWithSpaces(reorderedData);

    void applySpaceGroupReorder(
      reorderedData.map(currentGroup => currentGroup.id.toString()),
    );
  };

  const isLoadingAppData = useIsLoadingAppData();
  const { isCircleV4 } = useCircleV4();
  const isLargeScreen = useTailwindLgScreenMediaQuery();

  if (isLoading || isLoadingAppData) {
    return <CommunitySidebarSkeleton />;
  }

  return (
    <DndContext
      onSpaceGroupSortEnd={onSpaceGroupSortEnd}
      onSpaceSortEnd={onSpaceSortEnd}
      spaceGroupWithSpaces={spaceGroupWithSpaces}
    >
      {isCircleV4 && isLargeScreen ? (
        <SidebarV3 spaceGroupWithSpaces={spaceGroupWithSpaces} />
      ) : (
        <SidebarV2 spaceGroupWithSpaces={spaceGroupWithSpaces} />
      )}
    </DndContext>
  );
};
